import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { paper, fonts, easing, springs } from "../theme";
import { useNfcScan } from "../useNfcScan";
import type { NfcScanError } from "../useNfcScan";
import * as haptics from "../haptics";
import {
    MsScreen, MsBody, MsFooter,
    MsDisplay, MsDeck, MsEyebrow, MsRuleRed,
    MsBtn, MsBackButton, MaskHeadline,
    MsSpinner, MsFieldError,
} from "../ms";

/* ── Full-screen sheet ───────────────────────────────────────────────────── */

/**
 * Transparent by design: the caller unmounts the home steps while this is open,
 * so the app's one global backdrop shows through. Painting paper here would
 * occlude the grain and vignette along with the shared word field.
 */
const Overlay = styled(motion.div)`
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    flex-direction: column;
`;

const ScanBody = styled(MsBody)`
    justify-content: center;
    align-items: center;
    text-align: center;
    gap: 26px;
`;

const Headline = styled(MsDisplay)`
    font-size: 40px;
    line-height: 0.98;
`;

const HeadlineRule = styled(MsRuleRed)`
    margin: 14px auto 0;
`;

const Caption = styled(MsDeck)`
    max-width: 320px;
`;

/* ── Contactless motif — a deck tapped against the phone ─────────────────── */

const waveFade = keyframes`
    0%   { opacity: 0; }
    40%  { opacity: 0.9; }
    100% { opacity: 0; }
`;

const Motif = styled.svg`
    width: 168px;
    height: 168px;
    color: ${paper.ink};

    .wave {
        stroke: ${paper.red};
        animation: ${waveFade} 2.1s ${easing.standard} infinite;
    }
    .wave:nth-of-type(2) { animation-delay: 0.35s; }
    .wave:nth-of-type(3) { animation-delay: 0.7s; }

    @media (prefers-reduced-motion: reduce) {
        .wave { animation: none; opacity: 0.55; }
    }
`;

function ContactlessMotif() {
    return (
        <Motif viewBox="0 0 200 200" fill="none" aria-hidden="true">
            {/* Deck card, tilted, with the Misquote "M" */}
            <g transform="rotate(-6 62 100)">
                <rect
                    x="34" y="62" width="56" height="76" rx="7"
                    fill={paper.paperCool} stroke="currentColor" strokeWidth="3"
                />
                <text
                    x="62" y="112" textAnchor="middle"
                    fontFamily={fonts.serif} fontStyle="italic" fontWeight="800"
                    fontSize="44" fill="currentColor"
                >M</text>
            </g>
            {/* Contactless waves emanating toward the phone */}
            <path className="wave" d="M108 84 A24 24 0 0 1 108 116" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
            <path className="wave" d="M121 74 A40 40 0 0 1 121 126" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
            <path className="wave" d="M133 64 A56 56 0 0 1 133 136" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
        </Motif>
    );
}

/* ── Error slip ──────────────────────────────────────────────────────────── */

/* The shared slip, sized up a step: this one is the screen's main message
 * rather than a note beside a field. */
const ErrorSlip = styled(MsFieldError)`
    gap: 10px;
    max-width: 360px;
    font-size: 14px;
    border-radius: 12px;
    padding: 12px 14px;
`;

const stageAnim = {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
    transition: springs.reveal,
};

/** Centred stack for each stage's contents. */
const Stage = styled(motion.div)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 22px;
`;

/* ── Component ───────────────────────────────────────────────────────────── */

const ERROR_COPY: Record<NfcScanError, string> = {
    unsupported: "This device can't scan tags from the browser.",
    denied: "NFC access was blocked. Allow it for this site in your browser settings, then try again.",
    failed: "Couldn't read that tag. Hold the deck flat against the back of your phone and try again.",
};

type Stage =
    | { kind: "scanning" }
    | { kind: "validating" }
    | { kind: "error"; message: string };

interface Props {
    /** Resolve a scanned tag URL into a room. Rejects if the tag isn't ours. */
    onResolveUrl: (url: string) => Promise<void>;
    onClose: () => void;
}

/**
 * "Tap to host" scan sheet. Opened from the home-screen host tile on devices
 * that support Web NFC. Arms a scan on mount, validates the read tag against the
 * backend (reusing the same handoff as the passive URL open), and hands off to
 * the join-name step on success. Cancel / errors keep the user here.
 */
export default function ScanDeck({ onResolveUrl, onClose }: Props) {
    const [stage, setStage] = useState<Stage>({ kind: "scanning" });

    const handleError = useCallback((err: NfcScanError) => {
        setStage({ kind: "error", message: ERROR_COPY[err] });
    }, []);

    const handleUrl = useCallback((url: string) => {
        haptics.select();
        setStage({ kind: "validating" });
        onResolveUrl(url)
            .then(() => onClose())
            .catch(() => setStage({
                kind: "error",
                message: "This tag wasn't recognized. Give it another scan.",
            }));
    }, [onResolveUrl, onClose]);

    const { supported, armed, start } = useNfcScan(handleUrl, handleError);

    // Arm the scan on mount (and whenever we return to the scanning stage).
    useEffect(() => {
        if (stage.kind === "scanning") start();
    }, [stage.kind, start]);

    // A light tap the moment the reader arms, cueing that it's safe to tap now.
    useEffect(() => {
        if (armed) haptics.tap();
    }, [armed]);

    const retry = () => setStage({ kind: "scanning" });

    return (
        <Overlay
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
        >
            <MsScreen>
                <ScanBody>
                    <MsBackButton onClick={onClose} label="Close" />

                    <AnimatePresence mode="wait" initial={false}>
                        {stage.kind === "scanning" && !armed && (
                            <Stage key="preparing" {...stageAnim}>
                                <MsSpinner />
                                <div>
                                    <Headline>
                                        <MaskHeadline>Turning on NFC.</MaskHeadline>
                                    </Headline>
                                    <HeadlineRule />
                                </div>
                                <Caption>
                                    If your browser asks, allow NFC access — then wait for the go-ahead
                                    before tapping.
                                </Caption>
                                <MsEyebrow>Getting ready…</MsEyebrow>
                            </Stage>
                        )}

                        {stage.kind === "scanning" && armed && (
                            <Stage key="scanning" {...stageAnim}>
                                <ContactlessMotif />
                                <div>
                                    <Headline>
                                        <MaskHeadline>Tap your deck.</MaskHeadline>
                                    </Headline>
                                    <HeadlineRule />
                                </div>
                                <Caption>
                                    Hold a Misquote deck flat against the back of your phone.
                                </Caption>
                                <MsEyebrow tone="red">Ready — tap now</MsEyebrow>
                            </Stage>
                        )}

                        {stage.kind === "validating" && (
                            <Stage key="validating" {...stageAnim}>
                                <MsSpinner />
                                <Caption>Validating your tag…</Caption>
                            </Stage>
                        )}

                        {stage.kind === "error" && (
                            <Stage key="error" {...stageAnim}>
                                <div>
                                    <Headline>
                                        <MaskHeadline>No luck.</MaskHeadline>
                                    </Headline>
                                    <HeadlineRule />
                                </div>
                                <ErrorSlip>{stage.message}</ErrorSlip>
                            </Stage>
                        )}
                    </AnimatePresence>
                </ScanBody>

                <MsFooter>
                    {stage.kind === "error" ? (
                        <>
                            <MsBtn variant="red" onClick={retry}>Try again</MsBtn>
                            <MsBtn variant="ghost" onClick={onClose}>Cancel</MsBtn>
                        </>
                    ) : (
                        <MsBtn variant="ghost" onClick={onClose} disabled={stage.kind === "validating"}>
                            Cancel
                        </MsBtn>
                    )}
                    {!supported && (
                        <MsEyebrow>NFC isn't available here — you can still tap a deck to open the app.</MsEyebrow>
                    )}
                </MsFooter>
            </MsScreen>
        </Overlay>
    );
}
